// Chat API utilities for server-side event stream endpoint

export interface ChatMessage {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
}

export interface DeviceInfo {
    type: 'desktop' | 'mobile' | 'tablet';
    userAgent: string;
}

export interface UserPreferences {
    preferredPropertyTypes: string[];
    preferredAmenities: string[];
    priceRange: {
        min: number;
        max: number;
    };
    locationPreferences: string[];
}

export interface ClientContextualInfo {
    userId: string;
    device: DeviceInfo;
    userPreferences: UserPreferences;
}

export interface SearchDates {
    checkIn?: string;
    checkOut?: string;
}

export interface SearchGuests {
    adults?: number;
    children?: number;
    infants?: number;
    pets?: number;
}

export interface SearchPagination {
    page: number;
    limit: number;
}

export interface SearchRating {
    min: number;
    max: number;
}

export interface SearchPriceRange {
    min: number;
    max: number;
}

export interface SearchFilters {
    category?: string;
    typeOfPlace?: string[];
    propertyType?: string[];
    optionsForYourStay?: string[];
    priceRange?: SearchPriceRange;
    guests?: number;
    adults?: number;
    children?: number;
    infants?: number;
    pets?: number;
    bedrooms?: number;
    beds?: number;
    bathrooms?: number;
    amenities?: string[];
    services?: string[];
    houseRules?: string[];
    propertyTypes?: string[]; // Alias for propertyType for backward compatibility
    instantBook?: boolean;
    verified?: boolean;
    rating?: SearchRating;
}

export interface SearchParameters {
    location?: string;
    lat?: number;
    lng?: number;
    dates?: SearchDates;
    guests?: SearchGuests;
    currency?: string;
    pagination?: SearchPagination;
    filters?: SearchFilters;
}

export interface ChatNextRequest {
    action: 'next' | 'edit';
    conversation_id: string;
    parent_message_id?: string;
    model?: 'auto' | string;
    supported_encodings?: string[];
    enable_message_followups?: boolean;
    force_parallel_switch?: 'auto' | string;
    system_hints?: string[];
    timezone?: string;
    timezone_offset_min?: number;
    client_contextual_info?: ClientContextualInfo;
    messages: ChatMessage[];
    search_parameters?: SearchParameters;
}

export interface ChatNextResponse {
    // Response will be handled as server-sent events
    // Structure will be defined based on actual response
}

/**
 * Makes a request to the chat/next endpoint with server-sent events
 * Uses Amplify API configuration to handle CORS properly
 * @param request - The chat request payload
 * @returns Promise that resolves with the Response for handling SSE
 */
export const callChatNext = async (request: ChatNextRequest, signal?: AbortSignal): Promise<Response> => {
    // Validate that the last message is from user
    if (request.messages.length === 0 || request.messages[request.messages.length - 1].role !== 'user') {
        throw new Error('Last message must be a user message');
    }
    
    // Get the base URL from environment
    const baseUrl = process.env.REACT_APP_BACKEND_SEARCH || '';
    const endpoint = `${baseUrl}chat/next`;
    
    // Use fetch with proper CORS handling
    // Server uses wildcard CORS, so we don't include credentials
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Accept': 'text/event-stream',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal,
        // Don't include credentials when server uses wildcard CORS
        credentials: 'omit'
    });
    
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
};


/**
 * Helper function to create a chat message
 * @param role - The role of the message sender
 * @param content - The message content
 * @returns ChatMessage object
 */
export const createChatMessage = (role: ChatMessage['role'], content: string): ChatMessage => {
    return {
        role,
        content
    };
};

/**
 * Helper function to create device info
 * @param type - Device type
 * @param userAgent - User agent string
 * @returns DeviceInfo object
 */
export const createDeviceInfo = (type: DeviceInfo['type'], userAgent: string): DeviceInfo => {
    return {
        type,
        userAgent
    };
};

/**
 * Helper function to create user preferences
 * @param preferredPropertyTypes - Array of preferred property types
 * @param preferredAmenities - Array of preferred amenities
 * @param priceRange - Price range object
 * @param locationPreferences - Array of location preferences
 * @returns UserPreferences object
 */
export const createUserPreferences = (
    preferredPropertyTypes: string[],
    preferredAmenities: string[],
    priceRange: { min: number; max: number },
    locationPreferences: string[] = []
): UserPreferences => {
    return {
        preferredPropertyTypes,
        preferredAmenities,
        priceRange,
        locationPreferences
    };
};

/**
 * Helper function to create client contextual info
 * @param userId - User ID
 * @param device - Device information
 * @param userPreferences - User preferences
 * @returns ClientContextualInfo object
 */
export const createClientContextualInfo = (
    userId: string,
    device: DeviceInfo,
    userPreferences: UserPreferences
): ClientContextualInfo => {
    return {
        userId,
        device,
        userPreferences
    };
};

/**
 * Helper function to create search parameters
 * @param text - Search text
 * @param location - Location string
 * @param lat - Latitude
 * @param lng - Longitude
 * @param dates - Check-in and check-out dates
 * @param guests - Guest information
 * @param currency - Currency code
 * @param pagination - Pagination settings
 * @param filters - Search filters
 * @returns SearchParameters object
 */
export const createSearchParameters = (
    location?: string,
    lat?: number,
    lng?: number,
    dates?: SearchDates,
    guests?: SearchGuests,
    currency?: string,
    pagination?: SearchPagination,
    filters?: SearchFilters
): SearchParameters => {
    return {
        location,
        lat,
        lng,
        dates,
        guests,
        currency,
        pagination,
        filters
    };
};

/**
 * Helper function to create a complete chat request
 * @param conversationId - The conversation ID
 * @param messages - Array of chat messages
 * @param options - Optional parameters for the request
 * @returns ChatNextRequest object
 */
export const createChatRequest = (
    conversationId: string,
    messages: ChatMessage[],
    options?: {
        parentMessageId?: string;
        action?: 'next' | 'edit';
        model?: string;
        supportedEncodings?: string[];
        enableMessageFollowups?: boolean;
        forceParallelSwitch?: string;
        systemHints?: string[];
        timezone?: string;
        timezoneOffsetMin?: number;
        clientContextualInfo?: ClientContextualInfo;
        searchParameters?: SearchParameters;
    }
): ChatNextRequest => {
    return {
        action: options?.action || 'next',
        conversation_id: conversationId,
        parent_message_id: options?.parentMessageId,
        model: options?.model || 'auto',
        supported_encodings: options?.supportedEncodings || ['v1'],
        enable_message_followups: options?.enableMessageFollowups ?? true,
        force_parallel_switch: options?.forceParallelSwitch || 'auto',
        system_hints: options?.systemHints || [],
        timezone: options?.timezone || 'Australia/Brisbane',
        timezone_offset_min: options?.timezoneOffsetMin || 600,
        client_contextual_info: options?.clientContextualInfo,
        messages,
        search_parameters: options?.searchParameters
    };
};
